/** Tiny IndexedDB key-value store — drafts + offline upload queue (AP-16). */
const DB = 'ppp2';
const STORES = ['drafts', 'uploads'] as const;
export type StoreName = (typeof STORES)[number];

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => STORES.forEach((s) => {
      if (!req.result.objectStoreNames.contains(s)) req.result.createObjectStore(s);
    });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const req = fn(db.transaction(store, mode).objectStore(store));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
  });
}

export const idb = {
  /** Commit related keys together and resolve only on transaction completion. */
  batch: async (store: StoreName, entries: Array<{ key: string; value?: unknown; delete?: boolean }>): Promise<void> => {
    const db = await open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(store, 'readwrite');
      transaction.oncomplete = () => { db.close(); resolve(); };
      transaction.onabort = () => { db.close(); reject(transaction.error ?? new Error('IndexedDB transaction aborted')); };
      try {
        const target = transaction.objectStore(store);
        for (const entry of entries) {
          if (entry.delete) target.delete(entry.key);
          else target.put(entry.value, entry.key);
        }
      } catch (error) {
        // DataCloneError and other synchronous enqueue failures must abort
        // earlier writes too; IndexedDB's rollback operation is abort().
        transaction.abort();
        reject(error);
      }
    });
  },
  get: <T>(store: StoreName, key: string) => tx<T | undefined>(store, 'readonly', (s) => s.get(key)),
  set: (store: StoreName, key: string, value: unknown) => tx(store, 'readwrite', (s) => s.put(value, key)),
  del: (store: StoreName, key: string) => tx(store, 'readwrite', (s) => s.delete(key)),
  keys: (store: StoreName) => tx<string[]>(store, 'readonly', (s) => s.getAllKeys() as IDBRequest),
  /** Wipe every store — used on logout so a shared device retains none of the
   *  previous user's drafts (PII) or queued document blobs. */
  clearAll: async () => {
    for (const s of STORES) await tx(s, 'readwrite', (st) => st.clear());
  },
};
